import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackPixel } from "@/utils/metaPixel";
import { useIsMobile } from "@/lib/useIsMobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon, ArrowRight, ArrowLeft, CheckCircle2, Clock } from "lucide-react";

const ARTIST_TYPES = ["Independent Artist", "Band", "Musician / Session Player", "Singer-Songwriter", "DJ / Electronic Producer"];
const BUSINESS_TYPES = [
  "Record Label",
  "Recording Studio",
  "Music School / Academy",
  "Music Event Company",
  "Artist Management",
  "Music Venue",
  "Audio Equipment Brand",
];
const TIME_SLOTS = [
  "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM",
  "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM",
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const STEPS = ["Basics", "You Are", "Schedule", "Lock It"];

const stepVariants = {
  enter: (dir) => ({ x: dir > 0 ? 64 : -64, opacity: 0, scale: 0.985 }),
  center: { x: 0, opacity: 1, scale: 1 },
  exit: (dir) => ({ x: dir > 0 ? -64 : 64, opacity: 0, scale: 0.985 }),
};

export const ConsultationWizard = ({ audienceHint }) => {
  const isMobile = useIsMobile();
  // On mobile, skip scroll-triggered entrances entirely (iOS Safari can fail
  // to fire them inside transformed ancestors, leaving the section invisible).
  // `animate` explicitly drives the card to a visible state so even residual
  // hidden values (e.g. after a desktop->mobile resize) get cleared.
  const revealProps = (extra = {}) =>
    isMobile
      ? { initial: false, animate: { opacity: 1, y: 0, scale: 1 } }
      : {
          initial: { opacity: 0, y: 24, ...extra },
          whileInView: { opacity: 1, y: 0, ...(extra.scale ? { scale: 1 } : {}) },
          viewport: { once: true, amount: 0.1 },
          transition: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] },
        };
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    entity_type: "",
    links: "",
    preferred_date: null,
    preferred_time: "",
    feedback: "",
    consent: false,
  });

  // Order the entity groups based on which audience CTA was clicked
  const groups = useMemo(() => {
    const artistGroup = { label: "Artists & Musicians", options: ARTIST_TYPES };
    const businessGroup = { label: "Music Businesses", options: BUSINESS_TYPES };
    return audienceHint === "business" ? [businessGroup, artistGroup] : [artistGroup, businessGroup];
  }, [audienceHint]);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const getStepErrors = (s, f) => {
    const errs = {};
    if (s === 0) {
      if (!f.name.trim()) errs.name = "We need your name";
      if (!EMAIL_RE.test(f.email.trim())) errs.email = "Enter a valid email";
      const digits = f.phone.replace(/\D/g, "");
      if (digits.length < 7) errs.phone = "Enter a valid contact number";
    }
    if (s === 1) {
      if (!f.entity_type) errs.entity_type = "Pick what describes you best";
      if (!f.links.trim()) errs.links = "Drop at least one link — Instagram, Spotify, website…";
    }
    if (s === 2) {
      if (!f.preferred_date) errs.preferred_date = "Pick a date";
      if (!f.preferred_time) errs.preferred_time = "Pick a time";
    }
    if (s === 3) {
      if (!f.consent) errs.consent = "Please accept the Privacy Policy so we can process your details (DPDP Act, 2023)";
    }
    return errs;
  };

  const validateStep = (s) => {
    const errs = getStepErrors(s, form);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) return;
    setDirection(1);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => {
    setDirection(-1);
    setStep((s) => Math.max(s - 1, 0));
  };

  const submit = async () => {
    // Final validation across all steps; jump back to the first failing one
    for (const s of [0, 1, 2, 3]) {
      const errs = getStepErrors(s, form);
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        setStep(s);
        if (errs.consent) toast.error(errs.consent);
        return;
      }
    }
    setSubmitting(true);
    try {
      const utm = JSON.parse(sessionStorage.getItem("hs_utm") || "{}");
      await api.post("/consultations", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        entity_type: form.entity_type,
        preferred_date: format(form.preferred_date, "yyyy-MM-dd"),
        preferred_time: form.preferred_time,
        links: form.links.trim(),
        feedback: form.feedback.trim(),
        consent: form.consent,
        ...utm,
      });
      // Meta Pixel Lead event (fires when Pixel ID is configured)
      trackPixel("Lead", { content_name: "consultation_request", content_category: form.entity_type });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = done ? 100 : ((step + 1) / STEPS.length) * 100;

  return (
    <section id="consult" className="relative py-14 sm:py-20" data-testid="consultation-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          {...revealProps()}
          className="text-center mb-10 sm:mb-14"
        >
          <p className="font-mono-ui text-[11px] tracking-[0.35em] uppercase text-[color:var(--hs-lime)]">
            Free 20-minute consultation
          </p>
          <h2 className="mt-3 font-display font-display-rough uppercase text-3xl sm:text-4xl lg:text-6xl text-white leading-[0.95]">
            Get your <span className="text-[color:var(--hs-lime)]">growth plan</span>
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-sm sm:text-base text-[color:var(--hs-ink-dim)]">
            A senior Holdstill team member digs into your specific pain points, maps custom growth
            solutions and walks you through pricing. No fluff. No obligation.
          </p>
        </motion.div>

        <motion.div
          {...revealProps({ scale: 0.98 })}
          className="max-w-2xl mx-auto rounded-3xl border border-white/10 bg-black/40 shadow-[var(--shadow-elev-2)] overflow-hidden"
          data-testid="consultation-wizard"
        >
          {done ? (
            <div className="p-8 sm:p-12 text-center" data-testid="consultation-success">
              <div className="signal-lock mx-auto h-20 w-20 rounded-full bg-[color:rgba(202,255,26,0.12)] border border-[color:rgba(202,255,26,0.4)] flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-[color:var(--hs-lime)]" />
              </div>
              <h3 className="mt-6 font-display uppercase text-2xl sm:text-3xl text-white">Signal locked</h3>
              <p className="mt-3 text-sm sm:text-base text-[color:var(--hs-ink-dim)] max-w-md mx-auto">
                Your consult request is in. A senior Holdstill team member will reach out on{" "}
                <span className="text-white font-medium">{form.phone}</span> to confirm your slot:
              </p>
              <div className="mt-5 inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-black/30 px-5 py-3">
                <Clock className="h-4 w-4 text-[color:var(--hs-lime)]" />
                <span className="font-mono-ui text-sm text-white">
                  {form.preferred_date ? format(form.preferred_date, "EEE, dd MMM yyyy") : ""} · {form.preferred_time}
                </span>
              </div>
              <p className="mt-6 font-mono-ui text-[11px] tracking-[0.25em] uppercase text-[color:var(--hs-ink-dim)]">
                Holdstill Records · Music Growth Systems
              </p>
            </div>
          ) : (
            <>
              <div className="px-6 sm:px-10 pt-6 sm:pt-8">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[color:var(--hs-lime)]">
                    {String(step + 1).padStart(2, "0")} / {String(STEPS.length).padStart(2, "0")} · {STEPS[step]}
                  </span>
                  <span className="font-mono-ui text-[11px] text-[color:var(--hs-ink-dim)]">~60 seconds</span>
                </div>
                <Progress value={progress} className="h-1.5 bg-white/10" data-testid="consultation-progress" />
              </div>

              <div className="px-6 sm:px-10 py-7 sm:py-9 min-h-[320px]">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={step}
                    custom={direction}
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
                    className="space-y-5"
                  >
                    {step === 0 && (
                      <>
                        <Field label="Your name" error={errors.name}>
                          <Input
                            data-testid="consultation-name-input"
                            placeholder="e.g. Arjun Nair"
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            className="h-12 bg-black/30 border-white/15 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-[color:var(--hs-focus)]"
                          />
                        </Field>
                        <Field label="Email" error={errors.email}>
                          <Input
                            data-testid="consultation-email-input"
                            type="email"
                            placeholder="you@band.com"
                            value={form.email}
                            onChange={(e) => set("email", e.target.value)}
                            className="h-12 bg-black/30 border-white/15 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-[color:var(--hs-focus)]"
                          />
                        </Field>
                        <Field label="Contact number" error={errors.phone}>
                          <Input
                            data-testid="consultation-phone-input"
                            type="tel"
                            placeholder="+91 98765 43210"
                            value={form.phone}
                            onChange={(e) => set("phone", e.target.value)}
                            className="h-12 bg-black/30 border-white/15 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-[color:var(--hs-focus)]"
                          />
                        </Field>
                      </>
                    )}

                    {step === 1 && (
                      <>
                        <Field label="You are a…" error={errors.entity_type}>
                          <Select value={form.entity_type} onValueChange={(v) => set("entity_type", v)}>
                            <SelectTrigger
                              data-testid="consultation-entity-select"
                              className="h-12 bg-black/30 border-white/15 text-white rounded-xl focus:ring-[color:var(--hs-focus)]"
                            >
                              <SelectValue placeholder="Select what describes you best" />
                            </SelectTrigger>
                            <SelectContent className="bg-[color:var(--hs-bg-2)] border-white/15 text-white max-h-72">
                              {groups.map((group) => (
                                <SelectGroup key={group.label}>
                                  <SelectLabel className="text-[color:var(--hs-lime)] font-mono-ui text-[10px] tracking-[0.25em] uppercase">
                                    {group.label}
                                  </SelectLabel>
                                  {group.options.map((opt) => (
                                    <SelectItem key={opt} value={opt} className="focus:bg-white/10 focus:text-white">
                                      {opt}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              ))}
                              <SelectGroup>
                                <SelectItem value="Other" className="focus:bg-white/10 focus:text-white">
                                  Other
                                </SelectItem>
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field
                          label="Links to your profiles / social pages"
                          hint="Instagram, Spotify, YouTube, website — anything that shows your work"
                          error={errors.links}
                        >
                          <Textarea
                            data-testid="consultation-links-input"
                            placeholder={"instagram.com/yourband\nopen.spotify.com/artist/…"}
                            value={form.links}
                            onChange={(e) => set("links", e.target.value)}
                            rows={3}
                            className="bg-black/30 border-white/15 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-[color:var(--hs-focus)] resize-none"
                          />
                        </Field>
                      </>
                    )}

                    {step === 2 && (
                      <>
                        <Field label="Preferred date" error={errors.preferred_date}>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                data-testid="consultation-date-trigger"
                                className="h-12 w-full justify-start bg-black/30 border-white/15 text-left font-normal rounded-xl hover:bg-black/40 hover:text-white text-white"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4 text-[color:var(--hs-lime)]" />
                                {form.preferred_date ? (
                                  format(form.preferred_date, "EEEE, dd MMM yyyy")
                                ) : (
                                  <span className="text-white/40">Pick a date for your consult</span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-[color:var(--hs-bg-2)] border-white/15" align="start">
                              <Calendar
                                mode="single"
                                selected={form.preferred_date}
                                onSelect={(d) => set("preferred_date", d)}
                                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                                initialFocus
                                className="text-white"
                              />
                            </PopoverContent>
                          </Popover>
                        </Field>
                        <Field label="Preferred time (IST)" error={errors.preferred_time}>
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="consultation-time-grid">
                            {TIME_SLOTS.map((slot) => (
                              <button
                                key={slot}
                                type="button"
                                data-testid={`time-slot-${slot.replace(/[\s:]/g, "")}`}
                                onClick={() => set("preferred_time", slot)}
                                className={`h-10 rounded-xl border font-mono-ui text-xs btn-lift ${
                                  form.preferred_time === slot
                                    ? "bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] border-transparent font-semibold"
                                    : "bg-black/30 border-white/15 text-white/80 hover:border-[color:rgba(202,255,26,0.5)]"
                                }`}
                              >
                                {slot}
                              </button>
                            ))}
                          </div>
                        </Field>
                      </>
                    )}

                    {step === 3 && (
                      <>
                        <Field label="Anything specific you want us to know?" hint="Optional — your biggest pain point, goals, timelines…">
                          <Textarea
                            data-testid="consultation-feedback-input"
                            placeholder="e.g. We release consistently but streams have plateaued…"
                            value={form.feedback}
                            onChange={(e) => set("feedback", e.target.value)}
                            rows={3}
                            className="bg-black/30 border-white/15 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-[color:var(--hs-focus)] resize-none"
                          />
                        </Field>
                        <div className="rounded-2xl border border-white/10 bg-black/30 p-4 sm:p-5 space-y-2" data-testid="consultation-review">
                          <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[color:var(--hs-lime)]">Review</p>
                          <ReviewRow label="Name" value={form.name} />
                          <ReviewRow label="Contact" value={`${form.email} · ${form.phone}`} />
                          <ReviewRow label="You are" value={form.entity_type} />
                          <ReviewRow
                            label="Slot"
                            value={`${form.preferred_date ? format(form.preferred_date, "dd MMM yyyy") : ""} · ${form.preferred_time}`}
                          />
                        </div>
                        {/* DPDP Act 2023 — explicit, informed consent */}
                        <div className="rounded-2xl border border-white/10 bg-black/30 p-4 sm:p-5">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="consent"
                              data-testid="consultation-consent-checkbox"
                              checked={form.consent}
                              onCheckedChange={(v) => set("consent", Boolean(v))}
                              className="mt-0.5 border-white/30 data-[state=checked]:bg-[color:var(--hs-lime)] data-[state=checked]:text-[color:var(--hs-bg-0)] data-[state=checked]:border-transparent"
                            />
                            <label htmlFor="consent" className="text-xs sm:text-sm text-white/70 leading-relaxed cursor-pointer">
                              I consent to Holdstill Records collecting and processing the details above to schedule my
                              consultation and contact me about it, as described in the{" "}
                              <Link to="/privacy" target="_blank" className="text-[color:var(--hs-lime)] underline underline-offset-2" data-testid="consultation-privacy-link">
                                Privacy Policy
                              </Link>
                              . I can withdraw consent or request erasure anytime at info@holdstill.in.
                            </label>
                          </div>
                          {errors.consent && (
                            <p className="mt-2 text-xs text-[#ff7878]" data-testid="consent-error">
                              {errors.consent}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="px-6 sm:px-10 pb-7 sm:pb-9 flex items-center justify-between gap-3">
                {step > 0 ? (
                  <Button
                    variant="outline"
                    onClick={back}
                    data-testid="consultation-back-step-button"
                    className="btn-lift h-12 px-5 bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white rounded-xl"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                ) : (
                  <span />
                )}
                {step < STEPS.length - 1 ? (
                  <Button
                    onClick={next}
                    data-testid="consultation-next-step-button"
                    className="btn-lift glow-lime h-12 px-7 bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl"
                  >
                    Next <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={submit}
                    disabled={submitting}
                    data-testid="consultation-submit-button"
                    className="btn-lift glow-lime h-12 px-7 bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl disabled:opacity-60"
                  >
                    {submitting ? "Locking in…" : "Request Consultation"}
                  </Button>
                )}
              </div>
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ label, hint, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-white/90 mb-2">{label}</label>
    {children}
    {hint && !error && <p className="mt-1.5 text-xs text-white/40">{hint}</p>}
    {error && (
      <p className="mt-1.5 text-xs text-[#ff7878]" data-testid="field-error">
        {error}
      </p>
    )}
  </div>
);

const ReviewRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 text-sm">
    <span className="text-white/50 shrink-0">{label}</span>
    <span className="text-white text-right break-all">{value}</span>
  </div>
);
